import type {
  MarkingPortalScheduledTimes,
  MarkingPortalSession,
  MarkingPortalPersistedWorkJourney,
  MarkingPortalUserInfoFields,
} from '@src/models/interfaces/markingPortal.interface';
import type IAttendanceRecord from '@src/models/interfaces/attendanceRecord.interface';
import type IWorkJourneyConfig from '@src/models/interfaces/workJourneyConfig.interface';
import type IWorkAddress from '@src/models/interfaces/workAddress.interface';
import type { TUserSummary } from '@src/models/interfaces/user.interface';
import {
  buildMarkingProgress,
  getCompletedMarkingsFromRecord,
  withMarkingProgress,
} from '@src/models/mappers/markingPortalMarking';
import { formatNationalId } from '@src/packages/utils/nationalId';
import { DateUtil } from '@src/packages/utils/dates';
import { resolveUserDisplayName } from '@src/packages/utils/user';
import moment from 'moment-timezone';

type WorkJourneyUserSummary = Partial<
  Pick<TUserSummary, 'address' | 'displayName' | 'idCard' | 'firstName' | 'lastName'>
>;

type WorkJourneyFirestoreRecord = {
  user?: WorkJourneyUserSummary;
  workJourneyConfig?: IWorkJourneyConfig;
};

const SHIFT_DATE_TIME_FORMAT = `${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`;

// When no timezone is provided we keep the browser-local behavior (legacy workJourneyConfig path).
// The attendance-record path always passes an explicit display timezone.
const formatShiftDateTime = (value: unknown, timeZone?: string | null): string => {
  const millis = DateUtil.getTimestampMs(value);
  if (millis === null) {
    return '';
  }

  return DateUtil.toDateString(millis, SHIFT_DATE_TIME_FORMAT, timeZone || null);
};

const parseTimeStringToDate = (value?: string | null): Date | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }

  const parsed = new Date(`1970-01-01T${trimmed}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatShiftTime = (value?: string | null): string => {
  const parsed = parseTimeStringToDate(value);
  if (!parsed) {
    return '';
  }

  return DateUtil.toDateString(parsed, DateUtil.DEFAULT_TIME_FORMAT);
};

const formatBreakTimeRange = (start?: string | null, end?: string | null): string => {
  const startValue = formatShiftTime(start);
  const endValue = formatShiftTime(end);

  if (!startValue && !endValue) {
    return '';
  }

  if (startValue && endValue) {
    return `${startValue} - ${endValue}`;
  }

  return startValue ?? endValue ?? '';
};

// Tolerant on purpose: an attendance record can carry `address` either as an already-formatted
// string or as a work-address object. Mirrors TimeTrackerUtils.getAddressString for the object case.
const formatLocation = (
  address: (Partial<IWorkAddress> & { floorOffice?: string | null }) | string | null | undefined,
): string => {
  if (!address) {
    return '';
  }

  if (typeof address === 'string') {
    return address.trim();
  }

  const parts: string[] = [];
  const push = (value?: string | null) => {
    const trimmed = value?.trim();
    if (trimmed) {
      parts.push(trimmed);
    }
  };

  push(address.address);
  push(address.floorOffice);

  const commune = address.commune?.trim();
  const city = address.city?.trim();
  if (commune) {
    parts.push(commune);
  }
  if (city && city.toLowerCase() !== commune?.toLowerCase()) {
    parts.push(city);
  }

  push(address.region);
  push(address.zip);

  return parts.join(', ');
};

const buildJourneyDetails = (
  config: IWorkJourneyConfig | null | undefined,
): MarkingPortalSession['journey'] | undefined => {
  if (!config) {
    return undefined;
  }

  const timeZone = (config.workShift as { timeZone?: string } | undefined)?.timeZone;

  return {
    startsAt: formatShiftDateTime(config.shiftStartDate, timeZone),
    breakTime: formatBreakTimeRange(config.breakStartTime, config.breakEndTime),
    endsAt: formatShiftDateTime(config.shiftEndDate, timeZone),
    location: formatLocation(config.address as IWorkAddress | undefined),
  };
};

const formatTimestampTime = (value: unknown, timeZone?: string | null): string => {
  const millis = DateUtil.getTimestampMs(value);
  if (millis === null) {
    return '';
  }

  return DateUtil.toDateString(millis, DateUtil.DEFAULT_TIME_FORMAT, timeZone || null);
};

const formatBreakRangeFromTimestamps = (
  start: unknown,
  end: unknown,
  timeZone?: string | null,
): string => {
  const startValue = formatTimestampTime(start, timeZone);
  const endValue = formatTimestampTime(end, timeZone);

  if (!startValue && !endValue) {
    return '';
  }

  if (startValue && endValue) {
    return `${startValue} - ${endValue}`;
  }

  return startValue || endValue;
};

/**
 * Builds the journey details from a scheduled attendance record, the source the mobile app
 * uses: scheduledStartAt -> "comienza", scheduledEndAt -> "termina". These are absolute
 * instants, so they are formatted in the display timezone (kiosk/app default) rather than the
 * browser's local zone.
 */
const buildJourneyFromAttendanceRecord = (
  record: IAttendanceRecord,
  timeZone?: string | null,
): MarkingPortalSession['journey'] => ({
  startsAt: formatShiftDateTime(record.scheduledStartAt, timeZone),
  breakTime: formatBreakRangeFromTimestamps(
    record.scheduledBreakStartAt,
    record.scheduledBreakEndAt,
    timeZone,
  ),
  endsAt: formatShiftDateTime(record.scheduledEndAt, timeZone),
  location: formatLocation(record.address),
});

const buildScheduledTimesFromAttendanceRecord = (
  record: IAttendanceRecord,
): MarkingPortalScheduledTimes => ({
  startAt: DateUtil.getTimestampMs(record.scheduledStartAt),
  breakStartAt: DateUtil.getTimestampMs(record.scheduledBreakStartAt),
  breakEndAt: DateUtil.getTimestampMs(record.scheduledBreakEndAt),
  endAt: DateUtil.getTimestampMs(record.scheduledEndAt),
});

const buildScheduledTimesFromWorkJourneyConfig = (
  config: IWorkJourneyConfig,
): MarkingPortalScheduledTimes => {
  const timeZone =
    (config.workShift as { timeZone?: string } | undefined)?.timeZone ||
    DateUtil.getDefaultTimeZone();
  const startAt = DateUtil.getTimestampMs(config.shiftStartDate);
  const endAt = DateUtil.getTimestampMs(config.shiftEndDate);

  const toMillisOnShiftDay = (timeValue?: string | null, baseMs?: number | null): number | null => {
    if (baseMs == null || !timeValue?.trim()) {
      return null;
    }

    const day = moment.tz(baseMs, timeZone).format('YYYY-MM-DD');
    const combined = moment.tz(`${day} ${timeValue.trim()}`, 'YYYY-MM-DD HH:mm:ss', timeZone);
    return combined.isValid() ? combined.valueOf() : null;
  };

  return {
    startAt,
    breakStartAt: toMillisOnShiftDay(config.breakStartTime, startAt),
    breakEndAt: toMillisOnShiftDay(config.breakEndTime, startAt),
    endAt,
  };
};

const serializeWorkJourneyConfig = (config: IWorkJourneyConfig): IWorkJourneyConfig =>
  JSON.parse(JSON.stringify(config)) as IWorkJourneyConfig;

export const mapMarkingPortalUserInfoFields = (record: unknown): MarkingPortalUserInfoFields => {
  const data = (record ?? {}) as MarkingPortalUserInfoFields;
  return {
    organizationId: data.organizationId,
    workJourneyId: data.workJourneyId,
    workJourneyConfigId: data.workJourneyConfigId,
    workShiftId: data.workShiftId,
  };
};

export class MarkingPortalMapper {
  static toNoJourneySession(
    user?: WorkJourneyUserSummary,
    timeZone?: string | null,
  ): MarkingPortalSession {
    const displayName = resolveUserDisplayName(user);
    const rut = user?.idCard ? formatNationalId(user?.address?.country, user.idCard) : '';
    const progress = buildMarkingProgress([], timeZone);

    return {
      displayName,
      rut,
      hasWorkJourney: false,
      journeyStatus: 'noJourney',
      primaryActionLabelId: progress.primaryActionLabelId,
      showRegister: progress.showRegister,
      showFinishShift: false,
      completedMarkings: [],
      nextMarkingType: progress.nextMarkingType,
      timeline: progress.timeline,
    };
  }

  /**
   * Builds a session from the employee's scheduled attendance record for the day, mirroring the
   * mobile app. Display name / RUT come from the record itself (employeeFullname /
   * employeeIdentifier). The record already carries the scheduled instants, so the session is
   * self-contained and does not need a persisted workJourneyConfig.
   */
  static toSessionFromAttendanceRecord(
    record: IAttendanceRecord,
    timeZone?: string | null,
  ): MarkingPortalSession {
    const displayName = record.employeeFullname?.trim() || '';
    const rut = record.employeeIdentifier
      ? formatNationalId(
          record.employeeIdentifierCountryCode,
          record.employeeIdentifier,
        )
      : '';

    const base: MarkingPortalSession = {
      displayName,
      rut,
      hasWorkJourney: true,
      journeyStatus: 'notStarted',
      journey: buildJourneyFromAttendanceRecord(record, timeZone),
      scheduledTimes: buildScheduledTimesFromAttendanceRecord(record),
      primaryActionLabelId: 'markingPortal.marking.register',
      showRegister: true,
      showFinishShift: false,
      completedMarkings: [],
      nextMarkingType: null,
    };

    // Derive the FSM state from the markings already registered on the record so a resumed
    // session (e.g. page reload mid-shift) starts on the correct next marking.
    return withMarkingProgress(base, getCompletedMarkingsFromRecord(record), timeZone);
  }

  static normalizePersistedSession(
    session: MarkingPortalSession,
    workJourney: MarkingPortalPersistedWorkJourney | null,
  ): MarkingPortalSession {
    if (!session.hasWorkJourney || !session.journey || !workJourney?.workJourneyConfig) {
      return session;
    }

    const config = workJourney.workJourneyConfig;

    return {
      ...session,
      journey: buildJourneyDetails(config) ?? session.journey,
      scheduledTimes: session.scheduledTimes ?? buildScheduledTimesFromWorkJourneyConfig(config),
    };
  }

  static toPersistedWorkJourney(
    workJourney: WorkJourneyFirestoreRecord,
    userInfo: MarkingPortalUserInfoFields,
  ): MarkingPortalPersistedWorkJourney | null {
    const organizationId = userInfo.organizationId?.trim();
    const workJourneyId = userInfo.workJourneyId?.trim();
    const workJourneyConfigId = userInfo.workJourneyConfigId?.trim();
    const config = workJourney.workJourneyConfig;

    if (!organizationId || !workJourneyId || !workJourneyConfigId || !config) {
      return null;
    }

    return {
      organizationId,
      workJourneyId,
      workJourneyConfigId,
      workShiftId: userInfo.workShiftId?.trim() || undefined,
      workJourneyConfig: serializeWorkJourneyConfig(config),
    };
  }
}
